import { Box, Button, Collapse, Container, Divider, Grid, Typography } from '@mui/material'
import React, { useRef, useState } from 'react'
import { format, parseISO } from 'date-fns'
import MultiTyreView from '../tyre/MultiTyreView'
import { numberFormatter } from '../../../utils/formatters'
import { useNavigate } from 'react-router-dom'
import _ from 'lodash'
import WarrantyClaimView from '../claims/WarrantyClaimView'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import DeleteIcon from '@mui/icons-material/Delete'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { useAuth } from '../../../hooks/context/UserAuthContext'
import ExpandButton from '../../ui/ExpandButton'
import CustomerView from '../user/CustomerView'
import { useDeleteWarrantyRegistration } from '../../../hooks/api/warranty'
import { useSnackbar } from 'notistack'
import { useQueryClient } from 'react-query'

const WarrantyRegistrationView = ({
  data,
  refetch,
  tableView = false,
  showDealershipName = false,
  alwaysExpand = false,
  onDelete = (id) => {},
}) => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const onClaimClick = (id) => {
    navigate(`/submit-claim/${id}`)
  }
  const [expanded, setExpanded] = useState(alwaysExpand)
  const [viewClaims, setViewClaims] = useState(false)
  const claimsRef = useRef(null)

  const handleViewClaims = () => {
    if (!viewClaims && claimsRef?.current) {
      claimsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    setViewClaims((v) => !v)
  }

  return (
    <Grid container rowGap={1} sx={{ p: 2 }}>
      {!tableView ? (
        <>
          <Grid item xs={12} sx={{ display: 'flex' }}>
            <Grid sx={{ flexGrow: 1 }} container direction='row' gap={1}>
              <Grid item xs={12}>
                <Typography variant='subtitle1'>Vehicle Registration Number</Typography>
                <Typography variant='body2' color='textSecondary'>
                  {data.vehicleNumber}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant='subtitle1'>Tyre Purchase Date</Typography>
                <Typography variant='body2' color='textSecondary'>
                  {format(parseISO(data.dateOfPurchase), 'dd/MM/yyyy')}
                </Typography>
              </Grid>
              {showDealershipName && (
                <Grid item xs={12}>
                  <Typography variant='subtitle1'>Dealership Name</Typography>
                  <Typography variant='body2' color='textSecondary'>
                    {data.customer?.dealershipName}
                  </Typography>
                </Grid>
              )}
            </Grid>
            {!alwaysExpand && (
              <Box>
                <ExpandButton onClick={() => setExpanded(!expanded)} expanded={expanded} />
              </Box>
            )}
          </Grid>
          <Grid item xs={12}>
            <Collapse unmountOnExit in={expanded}>
              <Grid container rowGap={1}>
                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <Box display='flex' justifyContent='flex-end' gap={2}>
                    {data.claimsCount > 0 && (
                      <Button
                        onClick={() => handleViewClaims()}
                        startIcon={viewClaims ? <RemoveIcon /> : <AddIcon />}
                      >
                        {viewClaims ? 'Hide claims' : 'Show claims'}
                      </Button>
                    )}
                    {data?.claimable && (
                      <Button variant='outlined' onClick={() => onClaimClick(data._id)}>
                        Create Claim
                      </Button>
                    )}
                  </Box>
                  {user?.isAdmin && data?.claimsCount === 0 && (
                    <Box display='flex' justifyContent='flex-end' gap={2} my={1}>
                      <Button
                        variant='outlined'
                        color='error'
                        onClick={() => onDelete(data._id)}
                        startIcon={<DeleteIcon />}
                      >
                        Delete
                      </Button>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant='subtitle1'>Warranty Registration ID</Typography>
                  <Typography variant='body2' color='textSecondary'>
                    {data._id}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant='subtitle1'>Mileage on Purchase</Typography>
                  <Typography variant='body2' color='textSecondary'>
                    {numberFormatter.format(data.mileageOnPurchase)} km
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant='subtitle1'>Chassis Number</Typography>
                  <Typography variant='body2' color='textSecondary'>
                    {data.chassisNumber || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant='subtitle1'>Sales Invoice Number</Typography>
                  <Typography variant='body2' color='textSecondary'>
                    {data.salesInvoiceNumber}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={12} md={8}>
                  <Typography variant='subtitle1'>Date of Creation</Typography>
                  <Typography variant='body2' color='textSecondary'>
                    {format(parseISO(data.createdAt), 'dd/MM/yyyy')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <CustomerView data={data.customer} />
              </Grid>
            </Collapse>
          </Grid>
        </>
      ) : (
        <>
          <Grid item xs={12}>
            <Collapse unmountOnExit in={tableView || expanded}>
              <Box display='flex' justifyContent='flex-end' gap={2}>
                {data.claimsCount > 0 && (
                  <Button
                    onClick={() => handleViewClaims()}
                    startIcon={viewClaims ? <RemoveIcon /> : <AddIcon />}
                  >
                    {viewClaims ? 'Hide claims' : 'Show claims'}
                  </Button>
                )}
                {data?.claimable && (
                  <Button variant='outlined' onClick={() => onClaimClick(data._id)}>
                    Create Claim
                  </Button>
                )}
              </Box>
              {user?.isAdmin && data?.claimsCount === 0 && (
                <Box display='flex' justifyContent='flex-end' gap={2} my={1}>
                  <Button
                    variant='outlined'
                    color='error'
                    onClick={() => onDelete(data._id)}
                    startIcon={<DeleteIcon />}
                  >
                    Delete
                  </Button>
                </Box>
              )}
            </Collapse>
          </Grid>
          <CustomerView data={data.customer} />
        </>
      )}
      <Grid item xs={12}>
        <Collapse unmountOnExit in={tableView || expanded}>
          {data.salesInvoice && (
            <Grid item xs={12}>
              <Box display='flex' justifyContent='flex-end'>
                <Button
                  variant='outlined'
                  component='a'
                  href={data.salesInvoice.filePath}
                  target='_blank'
                  rel='noopener noreferrer'
                  startIcon={<FileDownloadIcon />}
                >
                  View Sales Invoice
                </Button>
              </Box>
            </Grid>
          )}
          {data.warrantyCertificate?.length > 0 && (
            <Grid item xs={12}>
              <Typography variant='subtitle1'>Warranty Certificates</Typography>
            </Grid>
          )}
          {data.warrantyCertificate?.length > 0 &&
            data.warrantyCertificate.map((cert) => (
              <Grid item xs={12} key={cert._id}>
                <Box display='flex' justifyContent='flex-end'>
                  <Button
                    variant='outlined'
                    component='a'
                    href={cert.filePath}
                    target='_blank'
                    rel='noopener noreferrer'
                    startIcon={<FileDownloadIcon />}
                  >
                    {cert.fileName}
                  </Button>
                </Box>
              </Grid>
            ))}
          <MultiTyreView tyres={data.tyres} />
        </Collapse>
      </Grid>
      {data.claimsCount > 0 && (
        <Grid ref={claimsRef} item xs={12}>
          <Collapse unmountOnExit in={tableView || expanded}>
            <Collapse unmountOnExit in={viewClaims}>
              <Typography variant='h6'>Claims</Typography>

              {_.uniqBy(
                Object.values(data.tyres).map(({ claim }) => claim),
                '_id'
              )
                .filter(Boolean)
                .map((claim) => (
                  <WarrantyClaimView key={claim._id} data={claim} warrantyRegistration={data} />
                ))}
            </Collapse>
          </Collapse>
        </Grid>
      )}
    </Grid>
  )
}

export default WarrantyRegistrationView
